import { Container, Sprite } from 'pixi.js';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {ComponentModuleViewConfig, DefaultEventsMap, CoreGameConfig} from '@gongxigames/framework/core/types';
import {inject, injectable, Installer} from '@gongxigames/framework/core/installer';
import {BaseView} from '@gongxigames/framework/slot/fragments/module/view/BaseView';
import {System} from '@gongxigames/framework/core/services/system/System';
import {ObjectBuilder} from '@gongxigames/framework/core/services/ObjectBuilder';
import {SpineUtils} from '@gongxigames/framework/core/services/SpineUtils';
import {DisplayObjectUtils} from '@gongxigames/framework/core/services/DisplayObjectUtils';
import {componentNameToString} from '@gongxigames/framework/core/utils/componentNameToString';

/**
 * abstract class every view must be extended from. also has possibility to send events within the module
 * usually events are needed to module further consideration of actions
 */
@injectable()
export abstract class View<EventsMap = DefaultEventsMap> extends BaseView<EventsMap> {
    protected _parent!: Container;

    protected readonly _container: Sprite = new Sprite();

    @inject(CoreDI.System)
    protected _system!: System;

    @inject(CoreDI.ObjectBuilder)
    protected _objectBuilder!: ObjectBuilder;

    @inject(CoreDI.SpineUtils)
    protected _spineUtils!: SpineUtils;

    @inject(CoreDI.DisplayObjectUtils)
    protected _displayObjectUtils!: DisplayObjectUtils;

    @inject(CoreDI.Installer)
    protected _installer!: Installer;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: CoreGameConfig;

    public configure(componentConfig: ComponentModuleViewConfig): void {
        super.configure(componentConfig);

        this.parent = this._system.app.stageV1;
    }

    public init(): void {
        super.init();

        this.addToParent();
    }

    protected setInitParameters(config: ComponentModuleViewConfig): void {
        this.container.name = componentNameToString(config.NAME);
        this.container.parentGroup = config.layer.group;
    }

    public show(): void {
        this._container.visible = true;
    }

    public hide(): void {
        this._container.visible = false;
    }

    public addToParent(): void {
        this._parent.addChild(this._container);
    }

    public removeFromParent(): void {
        this._parent.removeChild(this._container);
    }

    public isVisible(): boolean {
        return this._container.visible;
    }

    public get container(): Sprite {
        return this._container;
    }

    public get parent(): Container {
        return this._parent;
    }

    public set parent(value: Container) {
        this._parent = value;
    }

    public get objectBuilder(): ObjectBuilder {
        return this._objectBuilder;
    }

    public get spineUtils(): SpineUtils {
        return this._spineUtils;
    }

    public set name(value: string) {
        this.container.name = value;
    }

    public get name(): string {
        return this.container.name;
    }

    public getGameConfig<C extends CoreGameConfig>(): C {
        return this._gameConfig as C;
    }
}
